import type { SearchResultClickEvent } from '../result-page/search-tab/SearchResultClickEvent';
import type { ViewChangeEvent } from '../result-page/search-tab/ViewChangeEvent';
import type { SearchResultsPresentationEvent } from '../result-page/search-tab/SearchResultsPresentationEvent';
import type { TabAnalyticsEvent } from '../result-page/tabs-header/TabAnalyticsEvent';
import type { MoreTabsAnalyticsEvent } from '../result-page/tabs-header/MoreTabsAnalyticsEvent';
import type { PaginationEvent } from '../result-page/search-tab/PaginationEvent';
import type {UiaasSearchAnalyticsReportingSeComponent} from './se/uiaas-search-analytics-reporting-se.component';
import type {UiaasSearchAnalyticsReportingGuidedComponent} from './guided/uiaas-analytics-reporting-guided.component';
import type {UiaasSearchAnalyticsReportingSvsComponent} from './svs/uiaas-search-analytics-reporting-svs.component';
import type {AnalyticsGenericEvent} from '../../utils/analytics/AnalyticsGenericEvent';

type TrackingComponent =
    | UiaasSearchAnalyticsReportingSeComponent
    | UiaasSearchAnalyticsReportingSvsComponent
    | UiaasSearchAnalyticsReportingGuidedComponent;

export type AnalyticsEvent =
    | SearchResultClickEvent
    | ViewChangeEvent
    | SearchResultsPresentationEvent
    | TabAnalyticsEvent
    | MoreTabsAnalyticsEvent
    | PaginationEvent
    | AnalyticsGenericEvent;

export function getEnv(component?:TrackingComponent): String {
    //if passed explicitly, use it
    if (component && component.env) {
      return component.env;
    }
    //otherwise, rely on heuristic to guess which env are we
    const hostname = window.location.hostname;

    const production = [
      'www.se.com',
      'www.schneider-electric.cn',
      'www.apc.com',
    ];

    return production.indexOf(hostname) > -1 ? 'Production' : 'UAT';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getDigitalPlatform(component: TrackingComponent, ignored: CustomEvent): String {
    if (component && component.digitalPlatform) {
      return component.digitalPlatform;
    }

    const hostname = (window.location.hostname =
        window.location.hostname.replace('www.', ''));

    if (hostname == 'se.com') {
      return 'se.com';
    }

    if (hostname == 'schneider-electric.cn') {
      return 'se.com';
    }

    if (hostname == 'apc.com') {
      return 'apc.com';
    }

    const href = window.location.href;
    if (href.toLowerCase().indexOf('.se.com/myschneider/') !== -1) {
      return 'seamless';
    }

    if (href.toLowerCase().indexOf('amplifyapp.com/myschneider') !== -1) {
      return 'seamlessUAT';
    }

    // this is our playground for uiaas, it should be accounted as DEV env for Seamless
    if (href.startsWith('https://s3-assets-dev.search.se.com/uiaas-search/')) {
      return 'SearchUiaasDEV';
    }

    return 'se.com';
}

export function enrichment(component:TrackingComponent, event: CustomEvent) {
    let details = { ...event.detail };
    if (event.detail.event === 'virtualPageview') {
      details = {
        articleTitle: component.articleTitle || '',
        businessUnit: component.businessUnit || 'DCX', // weird to keep it, should be changed, DCX is no more || QUESTION
        digitalPlatform: getDigitalPlatform(component, event),
        digitalPlatformCountry: getCountry(component, event),
        pageLanguage: getLanguage(component, event),
        environment: getEnv(component),
        fromWebView: component.fromWebView || false, // true when using mobile app not website. In our case always false.
        pageTopCategory: component.pageTopCategory || 'Search',
        pageCategory: component.pageCategory || 'Search',
        // 'pageCategoryId': '', // not search props...
        // 'pageSubCategoryId': '',
        // 'pageSubSubCategory': '',
        // 'pageProductId': '',
        // 'pageRangeId': '',
        template: component.template || 'Search',
        pageTitle: component.pageTitle || document.title,
        ...details,
      };
      return details;
    }
    return details;
}

/**
 * Executes regular expression and tries to get country and language
 * @param url
 * returns:
 * ["/uk/en", "uk", "en", index: 18, input: "https://www.se.com/uk/en/search/sedna", groups: undefined]
 * or null
 */
function getLocaleFromURL(url) {
    return /\/([a-z]+)\/([a-z]+)/.exec(url);
}

export function getCountry(component: TrackingComponent, event: CustomEvent): String {
    if (component.digitalPlatformCountry) {
      return component.digitalPlatformCountry;
    }
    if (
      event.target instanceof HTMLUiaasSearchTabElement &&
        event.target.country
    ) {
      return event.target.country;
    }

    //now - heuristic for guessing from URL:
    const hostname = window.location.hostname;
    if (hostname == 'www.schneider-electric.cn') return 'CN';

    const locale_from_url = getLocaleFromURL(window.location.href);
    //should be: ["/uk/en", "uk", "en", index: 18, input: "https://www.se.com/uk/en/search/sedna", groups: undefined]
    if (locale_from_url && locale_from_url[1]) {
      try {
        return locale_from_url[1].toUpperCase();
        // upper, because the value should be uppercase
      } catch (ignore) {}
    }
}

export function getLanguage(component: TrackingComponent, event: CustomEvent): String {
    if (component.language) {
      return component.language;
    }
    if (
      event.target instanceof HTMLUiaasSearchTabElement &&
        event.target.language
    ) {
      return event.target.language;
    }

    //now - heuristic for guessing from URL:
    const hostname = window.location.hostname;
    if (hostname == 'www.schneider-electric.cn') return 'ZH';
    const locale_from_url = this.getLocaleFromURL(window.location.href);
    //should be: ["/uk/en", "uk", "en", index: 18, input: "https://www.se.com/uk/en/search/sedna", groups: undefined]
    if (locale_from_url && locale_from_url[1]) {
      try {
        return locale_from_url[2].toUpperCase();
        // upper, because the value should be uppercase
      } catch (ignore) {}
    }
}

export function sendToSearchGA(
  category: String,
  action: String,
  label: String,
  destinationURL?: String,
  siteSearchKeyword?: String,
  descForG = '',
  nonInteraction = true
): void {
    if (!label || label.trim().length == 0) {
      return;
    }

    try {
      if (descForG && descForG.trim().length > 0) {
        label = label + '###' + descForG;
      }
      // @ts-ignore
      if (window.ga) {
        let details = ['send', 'event', category, action, label, destinationURL, siteSearchKeyword, 1, {
          nonInteraction: nonInteraction,
        }
        ];
        if(!siteSearchKeyword) {
          details = details.filter(detail => detail !== siteSearchKeyword);
        }
        if(!destinationURL) {
          details = details.filter(detail => detail !== destinationURL);
        }
        // @ts-ignore
        window.ga(...details);
      }
    } catch (err) {
      err('Search sendToSearchGA err: ' + err.message);
    }
}

export function pushToDataLayer(details: Object): void {
    const dataLayer = window.dataLayer !== undefined ? window['dataLayer'] : [];
    if(!details?.['siteSearchKeyword']) {
      delete details?.['siteSearchKeyword'];
    }
    if(!details?.['destinationURL']) {
      delete details?.['destinationURL'];
    }
    const reportedEvent = {
      ...details,
    };

    if (dataLayer) {
      dataLayer.push(reportedEvent);
    }
}

function htmlentities(str:string): string {
    return str
      .toString()
      .replace('&', '&amp;')
      .replace('\'', '&#39;')
      .replace('"', '&quot;')
      .replace('<', '&lt;')
      .replace('>', '&gt;');
}

export function object2attributes(obj: Object = {}): string {
    return Object
      .entries(obj)
      .map(([key, value])=>[
        htmlentities(key),
        htmlentities(value)
      ])
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
}

export function camelize(text) {
    const a = text.toLowerCase()
      .replace(/[-_\s.]+(.)?/g, (_, c) => c ? c.toUpperCase() : '');
    return a.substring(0, 1).toLowerCase() + a.substring(1);
}
